import { DestroyRef, Injectable, computed, effect, inject, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import Cookies from 'js-cookie';
import { BehaviorSubject, Observable, Subject, filter, finalize, first, forkJoin, map, of, retry, switchMap, takeUntil, tap, timer } from 'rxjs';
import { LoadingService } from '@app/core/services/loading.service';
import { RoleService } from '@app/core/services/role.service';
import { ClaimPermissions, HomeRoutes, SiteClaimSection, UserClaimTypes, UserClaims } from '@app/enums';
import { Claim, IdentityRoleClaim, RoleSiteClaimsResponse, UserRoleResponse } from '@app/interfaces/auth';
import { UnitService } from '@app/shared/services/unit.service';
import { UserService } from '@app/shared/services/user.service';

const cookieDomain = (window.location.hostname === 'localhost' ? '' : '.') + window.location.hostname;
const isLoggingInKey = 'isLoggingIn';
const lastSessionCookieName = 'LastSession';
const unitIdKey = 'unitId';

enum TokenPayloadKeys {
	SessionId = 'sid',
	IssuedAt = 'iat',
	ExpiresAt = 'exp',
	Email = 'https://accounts.linq.com/email',
	FullName = 'https://accounts.linq.com/name',
	UnitId = 'tenantId',
	UserId = 'tenantUserId',
	RoleId = 'roleId',
	RoleName = 'roleName',
}

@Injectable()
export class AuthService {
	// many internal and external references
	authentication$ = new BehaviorSubject<AuthenticationInfo>(initialAuthInfo);

	private readonly isOpenIdAuthenticated = computed(() => this.oidcSecurityService.authenticated().isAuthenticated);

	private readonly unauthenticated$ = this.oidcSecurityService.isAuthenticated$.pipe(filter((result) => !result.isAuthenticated));

	private isInitializing = false;

	// Logging in via the OpenID provider involves redirects, so we need to track state across those redirects
	// so that we can load the auth data correctly once the user is redirected back to our app.
	private set isLoggingIn(value: boolean) {
		if (value) {
			this.loadingService.show();
		} else {
			this.loadingService.hide();
		}
		sessionStorage.setItem(isLoggingInKey, value ? 'true' : 'false');
	}

	private get isLoggingIn(): boolean {
		return sessionStorage.getItem(isLoggingInKey) === 'true';
	}

	// Logout doesn't redirect, so we can just use a simple boolean flag here.
	private isLoggingOut = false;

	readonly #destroyRef = inject(DestroyRef);

	constructor(
		private readonly router: Router,
		private readonly userService: UserService,
		private readonly roleService: RoleService,
		private readonly unitService: UnitService,
		private readonly oidcSecurityService: OidcSecurityService,
		private readonly loadingService: LoadingService,
	) {
		effect(() => {
			// Note that this derived signal does NOT trigger this effect for forceRefreshSession()
			const isAuthenticated = this.isOpenIdAuthenticated();

			const isInitializing = this.isInitializing;
			this.isInitializing = false;

			if (this.isLoggingIn) {
				if (isInitializing && !isAuthenticated) {
					// If this effect is triggered during initialization and we're in the middle of a login,
					// we likely just returned from the OIDC provider but don't have the auth info loaded yet.
					// Wait for the next effect run to check authentication again, rather than logging out the user.
					return;
				}

				this.isLoggingIn = false;
				if (isAuthenticated) {
					// Make sure we aren't tracking dependencies inside the method call
					untracked(() => this.onSuccessfulLogin());
				}
			}

			if (!isAuthenticated && !this.isLoggingOut) {
				// Make sure local session info is cleared. Logout does this already, so we skip it in that case.
				untracked(() => this.clearSessionInfo());
			}
			this.isLoggingOut = false;
		});

		this.authentication$
			.pipe(
				switchMap((auth: AuthenticationInfo) => {
					// refresh token 30 secs buffer before expiration
					const initialDelay = (auth.expiresIn - 30) * 1000;

					// Start after initialDelay, emitting 1 time
					return timer(initialDelay).pipe(
						switchMap(() => this.refreshSessionInfo()),
						takeUntil(this.unauthenticated$),
					);
				}),
			)
			.subscribe();
	}

	/**
	 * Private field storing the current OAuth access token.
	 * Updated when authentication is set or refreshed.
	 * @private
	 */
	#accessToken = '';

	// 1 external reference (AJS)
	loaded$ = new Subject<boolean>();

	private setAccessToken(accessToken: string): void {
		this.#accessToken = accessToken;
	}

	// multiple external references
	get accessToken(): string {
		return this.#accessToken;
	}

	// many references, both internal and external
	get authentication(): AuthenticationInfo {
		return this.authentication$.value;
	}

	// 1 external reference
	get isAuthenticated(): boolean {
		return !!this.authentication.accessToken;
	}

	set defaultUnitId(unitId: string) {
		const upperUnitId = unitId.toUpperCase();
		localStorage.setItem(unitIdKey, upperUnitId);
	}

	get defaultUnitId(): string {
		return localStorage.getItem(unitIdKey) || '';
	}

	private onSuccessfulLogin(): void {
		this.setSessionInfo().subscribe();
	}

	/**
	 * Retrieves the session id, which indicates whether the current session has just been refreshed, or
	 * whether the user has logged in with new credentials.
	 */
	private getCurrentSessionId(): Observable<string> {
		// The authentication result can survive app reloads, so use that to determine if there is still a valid
		// session available.
		return this.oidcSecurityService.getAuthenticationResult().pipe(
			switchMap((authResult) => {
				const hasIdToken = !!authResult.id_token;
				return hasIdToken
					? this.oidcSecurityService.getPayloadFromIdToken().pipe(map((payload) => payload[TokenPayloadKeys.SessionId] || ''))
					: of('');
			}),
		);
	}

	/**
	 * Store the session id in a cookie so if the user navigates away and back, we can detect when it changes.
	 */
	private setSessionCookie(): Observable<string> {
		const lastSessionId = Cookies.get(lastSessionCookieName);

		return this.getCurrentSessionId().pipe(
			switchMap((sessionId) => {
				if (sessionId && sessionId !== lastSessionId) {
					// Secure the cookie, even though we don't intend to pass it around.
					Cookies.set(lastSessionCookieName, sessionId, { domain: cookieDomain, secure: true, sameSite: 'strict' });
				}
				return of(sessionId);
			}),
		);
	}

	private clearSessionCookie(): void {
		Cookies.remove(lastSessionCookieName, { domain: cookieDomain });
	}

	// 1 external reference
	/**
	 * Initializes authentication, checking for existing session info, and reloading it as needed.
	 */
	initialize(): Promise<boolean> {
		this.isInitializing = true;
		if (this.isLoggingIn) {
			// Skip initialization if we're in the middle of a login, otherwise we might logout the user.
			this.loaded$.next(true);
			return Promise.resolve(true);
		}

		return new Promise((resolve) => {
			this.setSessionInfo()
				.pipe(first())
				.subscribe({
					complete: () => {
						this.loaded$.next(true);
						resolve(true);
					},
					error: () => {
						this.clearSessionInfo(true);
						this.loaded$.next(true);
						resolve(true);
					},
				});
		});
	}

	/**
	 * Initiates the login process by redirecting the user to the OIDC provider. The user will be redirected
	 * back to the app after logging in, at which point the auth info will be loaded and the user will be
	 * logged in automatically.
	 * @param unitId (required) The unit to log in to. This is needed so that custom actions in the OIDC
	 * provider can authenticate the user against the correct tenant and include this id as a claim in the
	 * access token.
	 */
	login(unitId: string): void {
		this.defaultUnitId = unitId;
		const authOptions = {
			customParams: {
				// This parameter is used to provide settings to the cross-app SSO post-login flow
				// eslint-disable-next-line @typescript-eslint/naming-convention
				auth_ctx: JSON.stringify({
					// eslint-disable-next-line @typescript-eslint/naming-convention
					tenant_id: this.defaultUnitId,
				}),
			},
		};

		this.isLoggingIn = true;
		this.oidcSecurityService.authorize(null, authOptions);
	}

	// multiple external references
	/**
	 * Logs the user out of the app and the OIDC provider, clearing all session info in the process. Used when
	 * the user needs to be explicitly logged out.
	 */
	logOut(): void {
		this.isLoggingOut = true;
		this.clearSessionInfo(true);
		this.navigateToLogin();
	}

	/**
	 * Logs the user out of the app and the OIDC provider, clearing all session info in the process. Used when
	 * the user needs to be forced to log out, regardless of whether authentication info is available, and we
	 * want to make sure they have to log in again to get a new access token with the correct claims.
	 */
	forceLogout(): void {
		this.clearSessionInfo();
		this.oidcSecurityService.logoffAndRevokeTokens().subscribe(() => {
			this.navigateToLogin();
		});
	}

	/**
	 * Clears authentication info without logging out of the OIDC provider. This is used when we want to clear
	 * our local auth state, but we don't want to log the user out of the OIDC provider (which triggers side effects).
	 */
	reset(): void {
		this.clearSessionInfo();
	}

	private setSessionInfo(): Observable<AuthenticationInfo | null> {
		return this.setSessionCookie().pipe(
			takeUntilDestroyed(this.#destroyRef),
			tap(() => this.loadingService.show()),
			switchMap((sessionId) => {
				return sessionId ? this.setAuthentication() : of(null);
			}),
			finalize(() => this.loadingService.hide()),
		);
	}

	private refreshSessionInfo(): Observable<AuthenticationInfo | null> {
		return this.oidcSecurityService.forceRefreshSession().pipe(
			takeUntilDestroyed(this.#destroyRef),
			tap(() => this.loadingService.show()),
			switchMap((loginResponse) => {
				return loginResponse.isAuthenticated ? this.setSessionCookie() : of('');
			}),
			switchMap((sessionId) => {
				return sessionId ? this.refreshAuthentication() : of(null);
			}),
			finalize(() => this.loadingService.hide()),
		);
	}

	private clearSessionInfo(revokeTokens = false): void {
		this.clearSessionCookie();
		if (this.isLoggingIn) {
			this.isLoggingIn = false;
		}
		this.authentication$.next({ ...initialAuthInfo, initialized: true });

		if (revokeTokens) {
			// Make sure there's something to revoke before calling logoffAndRevokeTokens, otherwise it will
			// keep restarting the app.
			this.oidcSecurityService
				.getAuthenticationResult()
				.pipe(
					filter((authResult) => !!authResult?.access_token),
					tap(() => this.loadingService.show()),
					switchMap(() => this.oidcSecurityService.logoffAndRevokeTokens()),
					finalize(() => this.loadingService.hide()),
				)
				.subscribe();
		}
	}

	private refreshAuthentication(): Observable<AuthenticationInfo> {
		return forkJoin({
			authResult: this.oidcSecurityService.getAuthenticationResult(),
			payload: this.oidcSecurityService.getPayloadFromAccessToken(),
		}).pipe(
			takeUntilDestroyed(this.#destroyRef),
			map(({ authResult, payload }) => {
				this.setAccessToken(authResult?.access_token);

				const authInfo: AuthenticationInfo = {
					...this.authentication,
					accessToken: authResult?.access_token,
					refreshToken: authResult?.refresh_token,
					expiresIn: authResult?.expires_in,
					expires: new Date(payload[TokenPayloadKeys.ExpiresAt] * 1000).toUTCString(),
					issued: new Date(payload[TokenPayloadKeys.IssuedAt] * 1000).toUTCString(),
				};
				this.authentication$.next(authInfo);

				return authInfo;
			}),
		);
	}

	private setAuthentication(): Observable<AuthenticationInfo> {
		return forkJoin({
			authResult: this.oidcSecurityService.getAuthenticationResult(),
			payload: this.oidcSecurityService.getPayloadFromAccessToken(),
		}).pipe(
			takeUntilDestroyed(this.#destroyRef),
			switchMap(({ authResult, payload }) => {
				this.setAccessToken(authResult?.access_token);

				const authInfo: AuthenticationInfo = {
					...initialAuthInfo,
					accessToken: authResult?.access_token,
					refreshToken: authResult?.refresh_token,
					expiresIn: authResult?.expires_in,
					expires: new Date(payload[TokenPayloadKeys.ExpiresAt] * 1000).toUTCString(),
					issued: new Date(payload[TokenPayloadKeys.IssuedAt] * 1000).toUTCString(),
					fullName: payload[TokenPayloadKeys.FullName],
					userName: payload[TokenPayloadKeys.Email],
					email: payload[TokenPayloadKeys.Email],
					userId: payload[TokenPayloadKeys.UserId],
					unitId: payload[TokenPayloadKeys.UnitId],
					role: payload[TokenPayloadKeys.RoleName],
					isGA: payload[TokenPayloadKeys.RoleName] === UserClaims.globalAdmin,
					userRoles: [UserClaims.authorized, UserClaims.all],
				};

				return of(authInfo);
			}),
			switchMap((authInfo) => this.loadUserClaims(authInfo)),
			tap((authInfo) => {
				authInfo.initialized = true;
				this.authentication$.next(authInfo);
			}),
		);
	}

	private loadUserClaims(authInfo: AuthenticationInfo): Observable<AuthenticationInfo> {
		return forkJoin({
			unitAuthInfo: this.unitService.getAuthInfo(),
			userRole: this.userService.getUserRole(authInfo.userId),
			claimsResponse: this.roleService.listSiteClaims(),
		}).pipe(
			retry(1),
			map(({ unitAuthInfo, userRole, claimsResponse }) => {
				authInfo.unitName = unitAuthInfo.UnitName;
				authInfo.pendoProfile = unitAuthInfo.PendoProfile;
				authInfo.achSetupId = unitAuthInfo.ACHSetupId;
				authInfo.useNetAccounts = unitAuthInfo.UseNetAccounts;
				authInfo.siteAccounting = unitAuthInfo.SiteAccounting;
				authInfo.homeState = unitAuthInfo.HomeState;

				if (userRole.isValid && claimsResponse.isValid) {
					this.setUserClaims(authInfo, userRole, claimsResponse);
				}

				return authInfo;
			}),
		);
	}

	private setUserClaims(authInfo: AuthenticationInfo, userRole: UserRoleResponse, claimsResponse: RoleSiteClaimsResponse): void {
		authInfo.links = userRole.Links || [];

		const userClaims = userRole.Claims;
		authInfo.siteAccess = userClaims.filter((c) => c.Type === UserClaimTypes.SITE);
		authInfo.accountAccess = userClaims.filter((c) => c.Type === UserClaimTypes.ACCOUNT);

		const userRoleClaims = userRole.Role.IdentityRoleClaims;

		authInfo.siteClaims = claimsResponse.siteClaims;

		const mapClaim = (claim: SiteClaim): ActiveClaim => ({ Module: claim.Module, Page: claim.Page });

		authInfo.systemActive = claimsResponse.siteClaims.filter((c) => c.Section === SiteClaimSection.system && c.Active === true).map(mapClaim);
		authInfo.lsbActive = claimsResponse.siteClaims.filter((c) => c.Section === SiteClaimSection.lsb && c.Active === true).map(mapClaim);

		if (authInfo.isGA) {
			if (authInfo.systemActive.length > 0 && !authInfo.userRoles.includes(SiteClaimSection.system)) {
				authInfo.userRoles.push(SiteClaimSection.system);
			}
			if (authInfo.lsbActive.length > 0 && !authInfo.userRoles.includes(SiteClaimSection.lsb)) {
				authInfo.userRoles.push(SiteClaimSection.lsb);
			}
		} else {
			let addedSystem = false;
			let addedLSB = false;

			userRoleClaims.forEach((claim) => {
				if (authInfo.systemActive.length > 0 && claim.ClaimSection.lastIndexOf(SiteClaimSection.system, 0) === 0 && !addedSystem) {
					authInfo.userRoles.push(SiteClaimSection.system);
					addedSystem = true;
				} else if (authInfo.lsbActive.length > 0 && claim.ClaimSection.lastIndexOf(SiteClaimSection.lsb, 0) === 0 && !addedLSB) {
					authInfo.userRoles.push(SiteClaimSection.lsb);
					addedLSB = true;
				}
			});
		}

		authInfo.systemClaims = userRoleClaims.filter((c) => c.ClaimSection === SiteClaimSection.system);
		authInfo.lsbClaims = userRoleClaims.filter((c) => c.ClaimSection === SiteClaimSection.lsb);
		authInfo.fieldClaims = userRoleClaims.filter((c) => c.ClaimSection === 'field');
	}

	private navigateToLogin(): void {
		setTimeout(() => this.router.navigateByUrl(HomeRoutes.Login), 500);
	}

	// 1 external AJS reference
	/** @deprecated Use the HomeRoutes.Login constant instead */
	getLoginPath(): string {
		return HomeRoutes.Login;
	}

	public navigationAuthInfo$: Observable<NavigationAuthInfo> = this.authentication$.pipe(
		filter((authInfo) => authInfo.initialized),
		map((authInfo) => ({
			isAuthenticated: !!authInfo.accessToken,
			isGlobalAdmin: authInfo.isGA,
			hasSystemAccess: authInfo.isGA || authInfo.userRoles.includes(SiteClaimSection.system),
			hasBookkeepingAccess: authInfo.isGA || authInfo.userRoles.includes(SiteClaimSection.lsb),
		})),
	);

	private get siteClaims(): SiteClaim[] {
		return this.authentication?.siteClaims || [];
	}

	// 1 external reference
	siteClaimByLink(link: string): SiteClaim {
		return this.siteClaims.find((x) => link.toLowerCase().includes(x.Link?.toLowerCase()));
	}

	// many external references
	isReadOnly(section: string, page: string, module: string): boolean {
		if (this.authentication.isGA) {
			return false;
		}

		let claims: IdentityRoleClaim[] = [];
		switch (section) {
			case SiteClaimSection.system:
				claims = this.authentication.systemClaims;
				break;
			case SiteClaimSection.lsb:
				claims = this.authentication.lsbClaims;
				break;
			default:
				claims = [];
				break;
		}

		const hasClaim = claims.find((c) => c.ClaimPage === page && c.ClaimModule === module);

		if (hasClaim && hasClaim.ClaimValue !== ClaimPermissions.ReadOnly) {
			return false;
		}

		return true;
	}

	// 2 external references, plus a test reference
	isReadOnlyByLink(link: string): boolean {
		if (this.authentication.isGA) {
			return false;
		}

		const siteClaim = this.siteClaimByLink(link);

		if (siteClaim) {
			return this.isReadOnly(siteClaim.Section, siteClaim.Page, siteClaim.Module);
		}
		return true;
	}

	// appears to have only 1 external reference
	isActive(section: string, module: string, page: string): boolean {
		let activeClaims: ActiveClaim[] = [];
		switch (section) {
			case SiteClaimSection.system:
				activeClaims = this.authentication.systemActive;
				break;
			case SiteClaimSection.lsb:
				activeClaims = this.authentication.lsbActive;
				break;
			default:
				activeClaims = [];
				break;
		}

		let active = activeClaims.length > 0;

		if (active && module) {
			active = activeClaims.some((c) => c.Module === module);
		}

		if (active && page) {
			active = activeClaims.some((c) => c.Page === page);
		}

		return active;
	}

	// multiple external references
	isAccessible(section: string, module: string, page: string): boolean {
		// we first have to make sure that it is Active
		const active = this.isActive(section, module, page);
		if (!active) {
			return false;
		}

		if (this.authentication.isGA) {
			return true;
		}

		let claims: IdentityRoleClaim[] = [];
		switch (section) {
			case SiteClaimSection.system:
				claims = this.authentication.systemClaims;
				break;
			case SiteClaimSection.lsb:
				claims = this.authentication.lsbClaims;
				break;
			default:
				claims = [];
				break;
		}

		let hasClaim = claims.length > 0;

		if (hasClaim && module) {
			hasClaim = claims.some((c) => c.ClaimModule === module);
		}

		if (hasClaim && page) {
			hasClaim = claims.some((c) => c.ClaimPage === page);
		}

		return hasClaim;
	}

	// 2 external references
	isAccessibleByLink(link: string): boolean {
		if (this.authentication.isGA) {
			return true;
		}

		const siteClaim = this.siteClaimByLink(link);
		if (siteClaim) {
			return this.isAccessible(siteClaim.Section, siteClaim.Module, siteClaim.Page);
		}

		return false;
	}

	// 1 external reference
	hasWriteAccess(section: string, module: string, page: string): boolean {
		return this.isAccessible(section, module, page) && !this.isReadOnly(section, page, module);
	}

	// 2 external references
	isFieldAccessible(fieldName: string): boolean {
		return this.hasFieldClaim(fieldName);
	}

	private hasFieldClaim(claimValue: string): boolean {
		if (this.authentication.isGA) {
			return true;
		}
		return this.authentication.fieldClaims.some((item) => item.ClaimValue === claimValue);
	}

	// 2 external references
	// TODO: Remove the section parameter once the AJS pages using it are replaced
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	isReceivableActive(section?: string): boolean {
		const sectionActive = this.authentication.lsbActive;
		return sectionActive.some((c) => c.Module === 'receivable');
	}

	// all external references were used by doc management and are currently disabled
	hasPageClaim(claims: IdentityRoleClaim[], page: string, section: string, module: string): string | null {
		let activeClaims: ActiveClaim[] = [];
		switch (section) {
			case SiteClaimSection.system:
				activeClaims = this.authentication.systemActive;
				break;
			case SiteClaimSection.lsb:
				activeClaims = this.authentication.lsbActive;
				break;
			default:
				activeClaims = [];
				break;
		}
		if (activeClaims.length > 0) {
			const pageActive = activeClaims.find((c) => c.Module === module && c.Page === page);
			if (!pageActive) {
				return null;
			}
		} else {
			return null;
		}

		if (this.authentication.isGA) {
			return UserClaims.allAccess;
		}

		const hasClaim = claims.find((c) => c.ClaimPage === page);

		if (hasClaim) {
			return hasClaim.ClaimValue;
		}

		return null;
	}

	// only 1 external reference
	canAccessAll(type: string): boolean {
		let claims: Claim[] = [];
		switch (type) {
			case UserClaimTypes.SITE:
				claims = this.authentication.siteAccess;
				break;
			case UserClaimTypes.ACCOUNT:
				claims = this.authentication.accountAccess;
				break;
		}

		let canAccessAll = false;

		if (claims && claims.length > 0) {
			canAccessAll = claims.some((c) => c.Value === UserClaimTypes.allAccess);
		}

		return canAccessAll;
	}

	// 1 external reference
	canAccessLink(url: string): boolean {
		if (this.authentication.isGA || (url.match(/\//g) || []).length < 2) {
			return true;
		}

		if (url.endsWith('system-settings')) {
			return this.authentication.systemActive.length > 0;
		}

		// When routing from ajs page to angular page the params aren't being included so need a secondary check
		const i = url.lastIndexOf('/');
		const lastItem = url.substring(i + 1);
		if (!isNaN(+lastItem) || this.isGuid(lastItem)) {
			url = url.substring(0, i);
		}

		const links = this.authentication?.links || [];
		url = url.length && url.startsWith('/') ? url.slice(1) : url;

		return links.includes(url);
	}

	private isGuid(value: string): boolean {
		const guidRegex = /^[0-9a-fA-F]{8}-([0-9a-fA-F]{4}-){3}[0-9a-fA-F]{12}$/;
		return guidRegex.test(value);
	}
}

export interface AuthenticationInfo {
	/** Indicates whether authentication has been initialized (either successfully authenticated or
	 * determined to be unauthenticated) */
	initialized: boolean;
	accessToken: string;
	refreshToken: string;
	expiresIn: number; // time in seconds
	expires: string;
	issued: string; // Tue, 05 Apr 2022 16:46:26 GMT
	/* eslint-disable-next-line @typescript-eslint/naming-convention */
	isGA: boolean;
	userId: string;
	fullName?: string;
	userName: string;
	unitId: string;
	unitName: string;
	siteAccounting: boolean;
	siteClaims: SiteClaim[];
	pendoProfile: string;
	email: string;
	homeState: string;
	achSetupId: number;
	useNetAccounts: boolean;
	role: string;
	userRoles: string[];
	systemActive: ActiveClaim[];
	lsbActive: ActiveClaim[];
	systemClaims: IdentityRoleClaim[];
	lsbClaims: IdentityRoleClaim[];
	fieldClaims: IdentityRoleClaim[];
	siteAccess: Claim[];
	accountAccess: Claim[];
	links: string[];
}

export interface SiteClaim {
	Link?: string;
	Section: string;
	Module: string;
	Page: string;
}

export interface ActiveClaim {
	Module: string;
	Page: string;
}

export interface NavigationAuthInfo {
	isAuthenticated: boolean;
	isGlobalAdmin: boolean;
	hasSystemAccess: boolean;
	hasBookkeepingAccess: boolean;
}

const initialAuthInfo: AuthenticationInfo = {
	initialized: false,
	accessToken: '',
	refreshToken: '',
	expiresIn: 1199,
	expires: '',
	issued: '',
	fullName: '',
	userName: '',
	userId: '',
	unitId: '',
	unitName: '',
	pendoProfile: '',
	// eslint-disable-next-line @typescript-eslint/naming-convention
	isGA: false,
	homeState: '',
	achSetupId: 0,
	useNetAccounts: false,
	siteAccounting: false,
	siteAccess: [],
	userRoles: [],
	siteClaims: [],
	systemActive: [],
	lsbActive: [],
	systemClaims: [],
	lsbClaims: [],
	fieldClaims: [],
	email: '',
	role: '',
	accountAccess: [],
	links: [],
};
